import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext/LanguageContext';
import { Link } from 'react-router-dom';
import { usePageView } from '../../hooks/usePageView';
import hero1Webp from './hero1.webp';
import hero2Webp from './hero2.webp';
import hero3Webp from './hero3.webp';
import hero1Jpg from './hero1.jpg';
import hero2Jpg from './hero2.jpg';
import hero3Jpg from './hero3.jpg';
import './Home.css';

const supportsWebP = document.documentElement.classList.contains('webp');
const HERO_IMAGES = supportsWebP
  ? [hero1Webp, hero2Webp, hero3Webp]
  : [hero1Jpg, hero2Jpg, hero3Jpg];
const INTERVAL = 5000;

function Home() {
  usePageView('home');
  const { t } = useLanguage();
  const h = t.home;
  const [current, setCurrent] = useState(0);
  // 初始只加载第一张，滑动后逐步解锁
  const [loaded, setLoaded] = useState(new Set([0]));

  const next = useCallback(() => {
    setCurrent((c) => {
      const n = (c + 1) % HERO_IMAGES.length;
      setLoaded((prev) => new Set([...prev, n]));
      return n;
    });
  }, []);
  const prev = () => {
    setCurrent((c) => {
      const n = (c - 1 + HERO_IMAGES.length) % HERO_IMAGES.length;
      setLoaded((prev2) => new Set([...prev2, n]));
      return n;
    });
  };
  const goTo = (i) => {
    setLoaded((prev) => new Set([...prev, i]));
    setCurrent(i);
  };

  useEffect(() => {
    const timer = setInterval(next, INTERVAL);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <div className="home">
      <Helmet>
        <title>首嘉/深圳瀚阳 - 专业货运服务</title>
        <meta
          name="description"
          content="首嘉/深圳瀚阳提供专业的国内外货运、仓储、供应链管理服务，安全高效，值得信赖。"
        />
        <meta property="og:title" content="首嘉/深圳瀚阳 - 专业货运服务" />
        <meta
          property="og:description"
          content="首嘉/深圳瀚阳提供专业的国内外货运、仓储、供应链管理服务。"
        />
        <meta property="og:url" content="https://www.sz-showjoy.com" />
        <meta name="keywords" content="物流,货运,国际物流,深圳物流,首嘉/深圳瀚阳,空运,包机,仓储,供应链" />
        <link rel="canonical" href="https://www.sz-showjoy.com" />
      </Helmet>
      <section className="hero">
        {HERO_IMAGES.map((img, i) => (
          <div
            key={i}
            className={`hero-slide ${i === current ? 'active' : ''}`}
            style={loaded.has(i) ? { backgroundImage: `url(${img})` } : {}}
          />
        ))}
        <div className="hero-overlay"></div>

        <button
          className="hero-arrow hero-arrow-left"
          onClick={prev}
          aria-label="previous"
        >
          &#8249;
        </button>
        <button
          className="hero-arrow hero-arrow-right"
          onClick={next}
          aria-label="next"
        >
          &#8250;
        </button>

        <div className="hero-dots">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              className={`hero-dot ${i === current ? 'active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`slide ${i + 1}`}
            />
          ))}
        </div>

        <div className="container hero-content">
          <h1 className="hero-title">{h.heroTitle}</h1>
          <p className="hero-subtitle">{h.heroSubtitle}</p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn-primary">
              {h.contactNow}
            </Link>
            <Link to="/services" className="btn-secondary">
              {h.learnMore}
            </Link>
          </div>
        </div>
      </section>

      <section className="section features">
        <div className="container">
          <h2 className="section-title">{h.advantagesTitle}</h2>
          <p className="section-subtitle">{h.advantagesSubtitle}</p>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>{h.fastDelivery}</h3>
              <p>{h.fastDeliveryDesc}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🛡️</div>
              <h3>{h.safety}</h3>
              <p>{h.safetyDesc}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3>{h.pricing}</h3>
              <p>{h.pricingDesc}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">👥</div>
              <h3>{h.team}</h3>
              <p>{h.teamDesc}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section services-preview">
        <div className="container">
          <h2 className="section-title">{h.coreTitle}</h2>
          <p className="section-subtitle">{h.coreSubtitle}</p>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-image">✈️</div>
              <h3>{h.intlFreight}</h3>
              <p>{h.intlFreightDesc}</p>
              <Link to="/services" className="service-link">
                {h.learnDetails}
              </Link>
            </div>
            <div className="service-card">
              <div className="service-image">📦</div>
              <h3>{h.warehouseService}</h3>
              <p>{h.warehouseDesc}</p>
              <Link to="/services" className="service-link">
                {h.learnDetails}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">20+</div>
              <div className="stat-label">{h.yearsExp}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">9+</div>
              <div className="stat-label">{h.clients}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">5,000㎡</div>
              <div className="stat-label">{h.cities}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">20,000T</div>
              <div className="stat-label">{h.satisfaction}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section cta">
        <div className="container">
          <div className="cta-content">
            <h2>{h.ctaTitle}</h2>
            <p>{h.ctaDesc}</p>
            <div className="cta-buttons">
              <Link to="/pricing" className="btn-primary">
                {h.getQuote}
              </Link>
              <Link to="/contact" className="btn-secondary">
                {h.contactUs}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
